import React, { useEffect, useState, useRef } from "react";
import {
  Switch,
  FormControlLabel,
  Button,
  Divider
} from "@material-ui/core";
import { useSelector, useDispatch } from "react-redux";
import apiClient from "../../auth/apiClient";
import SettingsService from "../../services/SettingsService";
import { setEntityProperty } from "../../state/slices/entities";
import { selectContextEntity, setEntityProperty as coreSetEntityProperty } from "../../state/slices/CoreEntity";
import { useCoreEntityContext } from "../../hooks/useCoreEntitySlice";
import { useStyles } from "./RequestMonitorThresolds.styles";
import { useEnqueueSnackbar } from "../../hooks/useEnqueueSnackbar";
import { useSettingDispatchChange } from "../Forms/Settings";
import * as Yup from "yup";
import ThresholdSection from "./ThresholdSection";

const settingsService = new SettingsService(apiClient);

const TIER_COLORS = {
  TIER1: "#a4c99d",
  TIER2: "#F3AB2C",
  TIER3: "#FE626B"
};

const CURRENT_TIER_COLORS = {
  TIER1: "#8fbc8f",
  TIER2: "#ffd700",
  TIER3: "#b22222"
};

const defaultColors = {
  total: [TIER_COLORS.TIER1, TIER_COLORS.TIER2, TIER_COLORS.TIER3],
  average: [TIER_COLORS.TIER1, TIER_COLORS.TIER2, TIER_COLORS.TIER3],
  current: [CURRENT_TIER_COLORS.TIER1, CURRENT_TIER_COLORS.TIER2, CURRENT_TIER_COLORS.TIER3]
};

const defaultTier = (key) => ({
  tier1Color: defaultColors[key][0],
  tier2Value: 0,
  tier3Value: 0,
  tier2Color: defaultColors[key][1],
  tier3Color: defaultColors[key][2]
});

const sectionSchema = Yup.object().shape({
  tier2Value: Yup.number()
    .moreThan(0, "Tier 2 must be greater than 0")
    .required("Tier 2 is required"),
  tier3Value: Yup.number()
    .required("Tier 3 is required")
    .when("tier2Value", (tier2Value, schema) =>
      schema.moreThan(tier2Value, "Tier 3 must be greater than Tier 2")
    )
});

const thresholdSchema = Yup.object().shape({
  total: sectionSchema,
  average: sectionSchema,
  current: sectionSchema
});

const RequestMonitorThresholds = ({ entityID, onClose }) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const enqueueSnackbar = useEnqueueSnackbar();
  const useCoreEntitySlice = useCoreEntityContext();
  const { dispatchSetting } = useSettingDispatchChange();
  const initialState = useRef(null);

  const settings = useSelector((state) =>
    useCoreEntitySlice
      ? selectContextEntity(state)?.settings
      : state?.entities?.Context?.settings
  );

  const data = settings?.find((x) => x.name === "requestmonitorthresholds")?.value;
  const enableSettingValue = settings?.find((x) => x.name === "enablerequestmonitorthresholds")?.value === "true";

  const [enabled, setEnabled] = useState(enableSettingValue);

  const [total, setTotal] = useState(defaultTier("total"));
  const [average, setAverage] = useState(defaultTier("average"));
  const [current, setCurrent] = useState(defaultTier("current"));
  const [errors, setErrors] = useState({});
  const [shouldValidate, setShouldValidate] = useState(false);

  const validateThresholds = (t, a, c) => {
    if (!enabled || !shouldValidate) return;
    thresholdSchema.validate({ total: t, average: a, current: c }, { abortEarly: false })
      .then(() => setErrors({}))
      .catch((err) => {
        const validationErrors = {};
        err.inner.forEach((e) => {
          const [section, field] = e.path.split(".");
          if (!validationErrors[section]) validationErrors[section] = {};
          validationErrors[section][field] = e.message;
        });
        setErrors(validationErrors);
      });
  };

  useEffect(() => {
    try {
      if (data) {
        const parsed = JSON.parse(data);
        const rm = parsed?.requestMonitor;
        if (!parsed || !rm) return;

        const state = {
          total: { ...defaultTier("total"), ...rm.total },
          average: { ...defaultTier("average"), ...rm.average },
          current: { ...defaultTier("current"), ...rm.current }
        };

        setTotal(state.total);
        setAverage(state.average);
        setCurrent(state.current);
        initialState.current = state;
      }
    } catch {
      enqueueSnackbar("Failed to parse settings", {
        variant: "error", tag: "FailedToParseSettings"
      });
    }
  }, [data]);

  useEffect(() => {
    setEnabled(enableSettingValue);
  }, [enableSettingValue]);

  const updateTotal = (val) => { setShouldValidate(true); setTotal(val); };
  const updateAverage = (val) => { setShouldValidate(true); setAverage(val); };
  const updateCurrent = (val) => { setShouldValidate(true); setCurrent(val); };

  useEffect(() => {
    validateThresholds(total, average, current);
  }, [total, average, current, enabled, shouldValidate]);

  useEffect(() => {
    if (enabled) {
      setShouldValidate(true);
    }
    setErrors({});
  }, [enabled]);

  const handleSave = async () => {
    if (enabled) {
      try {
        await thresholdSchema.validate({ total, average, current }, { abortEarly: false });
      } catch (err) {
        const validationErrors = {};
        err.inner.forEach((e) => {
          const [section, field] = e.path.split(".");
          if (!validationErrors[section]) validationErrors[section] = {};
          validationErrors[section][field] = e.message;
        });
        setErrors(validationErrors);
        return;
      }
    }
    setErrors({});

    let existingPref = {};
    try {
      if (data) existingPref = JSON.parse(data);
    } catch {
      enqueueSnackbar("Failed to parse existing threshold settings", {
        variant: "error", tag: "FailedToParseExistingthresholdsettings"
      });
    }

    const updatedPref = {
      ...existingPref,
      requestMonitor: { total, average, current }
    };

    const newSettings = settings.map((x) =>
      x.name === "requestmonitorthresholds"
        ? { ...x, value: JSON.stringify(updatedPref) }
        : x
    );

    let success = false;
    try {
      var response = await settingsService.updateSetting(entityID, {
        name: "setting.requestmonitorthresholds",
        value: JSON.stringify(updatedPref)
      });
      if (response.status == 200) {
        dispatch(
          useCoreEntitySlice
            ? coreSetEntityProperty({
              entityId: entityID,
              property: "settings",
              value: newSettings
            })
            : setEntityProperty({
              entityid: entityID,
              property: "settings",
              value: newSettings
            })
        );
        success = true;
      }
    } catch {
      enqueueSnackbar("Failed to save request monitor thresholds", {
        variant: "error", tag: "FailedToSaveRequestMonitorThresholds"
      });
    }

    success = await saveEnableRequestMonitorThresholdsSetting(success, enabled);

    if (success) {
      onClose();
    }
  };

  const saveEnableRequestMonitorThresholdsSetting = async (success, enableValue) => {
    if (enableValue !== enableSettingValue) {
      try {
        if (await dispatchSetting(entityID, "enablerequestmonitorthresholds", enableValue)) {
          success = true;
        } else {
          success = false;
          enqueueSnackbar("Failed to save enable request monitor thresholds setting", {
            variant: "error", tag: "FailedToSaveEnableRequestMonitorThresholds"
          });
        }
      } catch {
        success = false;
        enqueueSnackbar("Failed to save enable request monitor thresholds setting", {
          variant: "error", tag: "FailedToSaveEnableRequestMonitorThresholds"
        });
      }
    }
    return success;
  };

  return (
    <div className={classes.preferencesContainer}>
      <div className={classes.contentArea}>
        <FormControlLabel
          control={
            <Switch
              color="primary"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
          }
          label="Enable Request Monitor Thresholds"
        />
        <Divider />

        <ThresholdSection
          title="Total # of Requests"
          values={total}
          setValues={updateTotal}
          defaults={defaultColors.total}
          disabled={!enabled}
          errors={errors.total}
          classes={classes}
        />

        <ThresholdSection
          title="Average Wait Time"
          values={average}
          setValues={updateAverage}
          defaults={defaultColors.average}
          disabled={!enabled}
          errors={errors.average}
          classes={classes}
        />

        <ThresholdSection
          title="Current Wait Time"
          values={current}
          setValues={updateCurrent}
          defaults={defaultColors.current}
          disabled={!enabled}
          errors={errors.current}
          classes={classes}
        />
      </div>
        <Divider/>
      <div className={classes.btnGroup}>
        <Button
          variant="outlined"
          onClick={() => onClose()}
        >
          CANCEL
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={handleSave}
        >
          SAVE
        </Button>

      </div>
    </div>
  );
};

export default RequestMonitorThresholds;
